// Matriz de preços vigentes: peças em linhas, "Preço base" + cada hotel em
// colunas. Pop-up com rolagem nos dois eixos, colunas redimensionáveis e linhas
// ordenáveis (abre por Categoria › Tipo de Peça). Filtra por categoria e por
// quais hotéis mostrar; exporta o que está visível em .csv.

import { precoBaseVigente, precoVigente } from "../../domain/precificacao.ts";
import { formatReais } from "../../domain/money.ts";
import { hojeISO } from "../../domain/datas.ts";
import type { Store } from "../store.ts";
import { clear, el, novoEstadoTabela, openModal, tabelaPaginada, botao } from "../ui.ts";
import type { Coluna } from "../ui.ts";
import { multiSelect } from "./_multi-select.ts";
import { baixarMatrizCsv } from "./_precos-io.ts";

export function abrirMatrizPrecos(store: Store): void {
  openModal(
    "Preços Vigentes — base e hotéis",
    () => {
      const snap = store.get();
      const hoje = hojeISO();
      const catNome = new Map(snap.categorias.map((c) => [c.id, c.nome]));

      const hoteisAtivos = [...snap.clientes]
        .filter((c) => c.ativo)
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
      const catsUsadas = [...snap.categorias]
        .filter((c) => snap.tiposPeca.some((t) => t.ativo && t.categoriaId === c.id))
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

      const msCategoria = multiSelect({
        rotulo: "Categoria",
        itens: catsUsadas.map((c) => ({ id: c.id, nome: c.nome })),
        todosPorPadrao: true,
        aoMudar: () => desenhar(),
      });
      const msHoteis = multiSelect({
        rotulo: "Hotéis",
        itens: hoteisAtivos.map((h) => ({ id: h.id, nome: h.nome })),
        aoMudar: () => desenhar(),
      });

      const est = novoEstadoTabela(1); // Categoria ▲
      est.size = 50;

      const alvo = el("div");

      const desenhar = (): void => {
        const hotelIds = new Set(msHoteis.selecionados());
        const hoteis = hoteisAtivos.filter((h) => hotelIds.has(h.id));

        const catIds = new Set(msCategoria.selecionados());
        const tipos = [...snap.tiposPeca]
          .filter((t) => t.ativo)
          .filter((t) => catIds.size === 0 || catIds.has(t.categoriaId))
          .sort((a, b) => {
            const c = (catNome.get(a.categoriaId) ?? "").localeCompare(
              catNome.get(b.categoriaId) ?? "",
              "pt-BR",
            );
            return c !== 0 ? c : a.nome.localeCompare(b.nome, "pt-BR");
          });

        const colunas: Coluna[] = [
          { t: "Tipo de Peça", w: 220 },
          { t: "Categoria", w: 130 },
          { t: "Preço base", w: 120 },
          ...hoteis.map((h): Coluna => ({ t: h.nome, w: 140 })),
        ];

        const celula = (valor: number | null, herdado: boolean): HTMLElement =>
          el(
            "td",
            {
              class: valor == null || herdado ? "muted num" : "num",
              "data-sort": valor == null ? "-1" : String(valor),
              title: herdado ? "este hotel ainda usa o preço base" : "",
            },
            valor == null
              ? "—"
              : herdado
                ? `${formatReais(valor)} ·`
                : formatReais(valor),
          );

        const linhas = tipos.map((t) => {
          const base = precoBaseVigente(snap, t.id, hoje);
          return el(
            "tr",
            {},
            el("td", {}, t.nome),
            el("td", { class: "muted" }, catNome.get(t.categoriaId) ?? "—"),
            celula(base, false),
            ...hoteis.map((h) => {
              const r = precoVigente(snap, h.id, t.id, hoje);
              return r.pendente
                ? celula(null, false)
                : celula(r.valorCentavos, r.origem === "base");
            }),
          );
        });

        clear(alvo);
        alvo.append(
          tabelaPaginada("matriz-precos", colunas, linhas, est, desenhar),
        );
      };
      desenhar();

      const exportarCsv = botao(
        "secundario",
        {},
        "Exportar .csv",
      );
      exportarCsv.addEventListener("click", () => {
        const catIds = new Set(msCategoria.selecionados());
        const tipoIds = new Set(
          snap.tiposPeca
            .filter((t) => t.ativo && (catIds.size === 0 || catIds.has(t.categoriaId)))
            .map((t) => t.id),
        );
        baixarMatrizCsv(snap, { tipoIds, hotelIds: msHoteis.selecionados() });
      });

      return el(
        "div",
        { class: "matriz-precos" },
        el(
          "div",
          { class: "matriz-barra" },
          msCategoria.element,
          msHoteis.element,
          el("div", { class: "matriz-barra-fim" }, exportarCsv),
        ),
        el(
          "p",
          { class: "muted" },
          "Preço que vale hoje. O “·” marca o hotel que ainda usa o preço base. " +
            "Arraste a divisória para redimensionar; clique no cabeçalho para reordenar.",
        ),
        alvo,
      );
    },
    "amplo",
  );
}
